"use client";

import { useUser } from "@/lib/auth/session";
import {
	getPostReactions,
	getReactionTypes,
	updatePostReactions,
} from "@/lib/blog/reactions";
import type { BlogPostReaction, ReactionType } from "@/lib/supabase/types";
import { cn, reactionEmojiCodes, reactionToEmoji } from "@/lib/utils";
import { Emoji, EmojiStyle, Theme } from "emoji-picker-react";
import { SmilePlus } from "lucide-react";
import dynamic from "next/dynamic";
import type React from "react";
import { useCallback, useEffect, useState } from "react";
import { Spinner } from "../ui/spinner";

const Picker = dynamic(() => import("emoji-picker-react"), { ssr: false });

interface ReactionPickerProps {
	postId: string;
	showButton?: boolean;
	expanded?: boolean;
}

export default function ReactionPicker({
	postId,
	showButton = false,
	expanded = false,
}: ReactionPickerProps) {
	const { user, isUserLoading, error } = useUser();

	const [showPicker, setShowPicker] = useState(false);
	const [isClient, setIsClient] = useState(false);
	const [reactions, setReactions] = useState<BlogPostReaction[]>([]);
	const [reactionTypes, setReactionTypes] = useState<ReactionType[]>([]);

	const handleShowPicker = (
		e:
			| React.MouseEvent<HTMLButtonElement | HTMLDivElement>
			| React.KeyboardEvent<HTMLButtonElement | HTMLDivElement>,
	) => {
		e.preventDefault();
		setShowPicker(true);
	};

	const fetchReactions = useCallback(async () => {
		const rs = await getPostReactions(postId);
		if (!rs) {
			console.error("No updated reactions found");
			return;
		}
		setReactions(rs);
	}, [postId]);

	const fetchReactionTypes = useCallback(async () => {
		const rs = await getReactionTypes();
		if (!rs) {
			console.error("No reaction types found");
			return;
		}
		setReactionTypes(rs);
	}, []);

	const handleReactionClick = async (emoji: string) => {
		const result = await updatePostReactions(postId, user?.id || null, emoji);

		if (result.success && !result.error) {
			// Refresh the reactions data after successful reaction
			await fetchReactions();
			setShowPicker(false); // Close the picker after successful reaction
		}
	};

	// find top 2 reaction names, sort by count
	const topReactionsIds = reactions
		.reduce((acc, reaction) => {
			if (!acc.some((item) => item === reaction.reaction_type_id)) {
				acc.push(reaction.reaction_type_id);
			}
			return acc.slice(0, 2);
		}, [] as string[])
		.sort((a, b) => {
			const aCount = reactions.filter(
				(reaction) => reaction.reaction_type_id === a,
			).length;
			const bCount = reactions.filter(
				(reaction) => reaction.reaction_type_id === b,
			).length;
			return bCount - aCount;
		});

	const topReactions = topReactionsIds.map((id) =>
		reactionTypes.find((rt) => rt.id === id),
	);

	useEffect(() => {
		setIsClient(true);
		fetchReactions();
		fetchReactionTypes();
	}, [fetchReactions, fetchReactionTypes]);

	if (isUserLoading) {
		return <Spinner className="size-3 self-center" />;
	}
	if (error) {
		console.error(error);
	}

	return (
		<div
			className="relative gap-1 items-center cursor-pointer"
			onMouseEnter={handleShowPicker}
			onMouseLeave={() => {
				setTimeout(() => setShowPicker(false), 2000);
			}}
			onClick={handleShowPicker}
			onKeyUp={(e) => {
				handleShowPicker(e);
			}}
			onKeyDown={(e) => {
				handleShowPicker(e);
			}}
		>
			<div className="flex flex-row gap-1 items-center">
				{showButton && (
					<button
						type="button"
						onClick={handleShowPicker}
						className={cn(
							"inline-flex items-center rounded-full p-1 text-xs font-bold bg-background/40 hover:bg-background/60 hover:border-primary hover:font-extrabold",
							expanded ? "text-primary" : "text-white",
						)}
					>
						<SmilePlus className="size-5" />
					</button>
				)}
				<div className="flex -space-x-2 overflow-hidden items-center">
					{topReactions.map((reaction) => (
						<div
							key={`${postId}-${reaction?.id || reaction?.name || Math.random()}`}
							className={cn(
								"text-left rounded-2xl w-fit bg-background/80 p-1 font-sans text-xs font-light",
								"transition-all hover:bg-background/60 hover:border-primary hover:rotate-12",
								expanded ? "text-primary" : "text-white",
							)}
						>
							{isClient && reaction && (
								<Emoji
									unified={reactionToEmoji(reaction.name)}
									size={expanded ? 24 : 18}
								/>
							)}
						</div>
					))}
				</div>
				<span
					className={`flex flex-row gap-1 text-sm font-semibold ${expanded ? "text-primary" : "text-white"}`}
				>
					<span className="sr-only">Total reactions:</span>
					{reactions.length}
					{expanded ? (reactions.length > 0 ? " reactions" : " reaction") : ""}
				</span>
				{showPicker && (
					<div className="absolute mt-[-82px] z-50 transition duration-300 ease-in data-closed:opacity-0">
						<Picker
							theme={Theme.LIGHT}
							allowExpandReactions={false}
							emojiStyle={EmojiStyle.NATIVE}
							reactionsDefaultOpen={true}
							reactions={reactionEmojiCodes()}
							onReactionClick={(emoji) => handleReactionClick(emoji.emoji)}
						/>
					</div>
				)}
			</div>
		</div>
	);
}
